import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

export interface SkeletonProps {
  variant?: 'text' | 'rectangular' | 'circular';
  width?: string | number;
  height?: string | number;
  animation?: 'wave' | false;
  className?: string;
}

const shimmerKeyframe = keyframes`
  0% {
    transform: translateX(-200%);
  }
  100% {
    transform: translateX(200%);
  }
`;

const SkeletonRoot = styled.span<{
  $variant: 'text' | 'rectangular' | 'circular';
  $width?: string | number;
  $height?: string | number;
}>`
  position: relative;
  overflow: hidden;
  display: block;

  /* Base neutral background — visible on light cards */
  background: #e3e3e3;

  border-radius: ${(p) => (p.$variant === 'circular' ? '50%' : '10px')};

  width: ${(p) =>
    typeof p.$width === 'number' ? `${p.$width}px` : (p.$width ?? '100%')};

  height: ${(p) =>
    typeof p.$height === 'number'
      ? `${p.$height}px`
      : (p.$height ??
        (p.$variant === 'text'
          ? '1rem'
          : p.$variant === 'circular'
            ? '40px'
            : '120px'))};

  /* HIGH-CONTRAST WIDE SHIMMER LAYER */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;

    /* Make shimmer HUGE → always visible */
    width: 250%;
    height: 100%;

    /* STRONG tilted shine like LinkedIn/Instagram */
    background: linear-gradient(
      35deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.25) 40%,
      rgba(255, 255, 255, 0.7) 50%,
      rgba(255, 255, 255, 0.25) 60%,
      rgba(255, 255, 255, 0) 100%
    );

    animation: ${shimmerKeyframe} 1.3s ease-in-out infinite;
  }
`;

export const Skeleton = ({
  variant = 'text',
  width,
  height,
  className,
}: SkeletonProps) => {
  return (
    <SkeletonRoot
      $variant={variant}
      $width={width}
      $height={height}
      className={className}
    />
  );
};
